/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext } from "react";
import produce, { setAutoFreeze } from "immer";
import { useToasts } from "react-toast-notifications";
import DatePicker from "react-datepicker";
import { useHistory } from "react-router-dom";
import interrogationObject from "../../assets/interrogationObject.json";
import { LayoutContext } from "../../components/layout";
import fetchHelper from "../../services/fetchHelper";
setAutoFreeze(false);

const CreateIRModal = ({ patient }) => {
  const { user, intrOrderInfo, setIntrOrderInfo } = useContext(LayoutContext);
  const { addToast } = useToasts();
  const [devices, setDevices] = useState([]);
  const [interrogation, setInterrogation] = useState(interrogationObject);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const history = useHistory();

  useEffect(() => {
    if (patient) {
      getSelectedDevices();
      setInterrogation((prev) =>
        produce(prev, (draft) => {
          draft.interrogation.routingLocId = patient?.patientLocation[0]?.location?.id;
          draft.interrogation.interrogationType = "POC";
          draft.interrogation.patient.id = patient?.patientLocation[0]?.patient.id;
          draft.patientLocation = {
            id: patient?.patientLocation[0]?.id,
            location: {
              id: patient?.patientLocation[0]?.location?.id,
              name: patient?.patientLocation[0]?.location?.name,
            },
            mrn: patient?.patientLocation[0]?.mrn,
          };
          draft.patient = patient?.patientLocation[0]?.patient;
          draft.patient.gender = patient?.gender;
          if (draft.patient.dob) draft.patient.dob = new Date(draft.patient.dob);
          delete draft.patient.patientState;
        })
      );
    }
  }, [patient]);

  useEffect(() => {
    setInterrogation((prev) =>
      produce(prev, (draft) => {
        if (intrOrderInfo?.irId) {
          draft.interrogationOrderId = intrOrderInfo?.irId;
          draft.interrogation.interrogationDate = intrOrderInfo?.irDate;
          draft.interrogation.visitNumber = intrOrderInfo?.irVisitNumber;
        } else {
          delete draft.interrogationOrderId;
        }
      })
    );
  }, [intrOrderInfo]);

  useEffect(() => {
    if (user) {
      setInterrogation((prev) =>
        produce(prev, (draft) => {
          draft.interrogation.orderingPhysician = { id: user.id };
        })
      );
    }
  }, [user]);

  const getSelectedDevices = async () => {
    try {
      const devices = await fetchHelper.get({
        url: `/patientmanagement/rest/devices/findByPatientId/${patient?.id}`,
      });
      setDevices(devices);
      if (devices.length)
        setInterrogation((prev) =>
          produce(prev, (draft) => {
            draft.interrogation.device.manufacturer = devices[0].manufacturerName;
            draft.interrogation.device.serialNumber = devices[0].serialNumber;
            draft.interrogation.device.type = devices[0].deviceType;
            draft.interrogation.device.modelName = devices[0].modelName;
          })
        );
    } catch (error) {
      console.log(error);
    }
  };

  const onCreateInterrogation = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("genToken") && JSON.parse(localStorage.getItem("genToken")).token;
      const resp = await fetch(`/patientmanagement/rest/interrogations/submit`, {
        method: "POST",
        headers: { token, "Content-Type": "application/json", "user-interaction": "sync" },
        body: JSON.stringify(interrogation),
      });
      const json = await resp.json();
      if (resp.ok) {
        setIsLoading(false);
        setIntrOrderInfo({});
        history.push(`/dashboard3/${json.id}`);
      } else {
        throw json;
      }
    } catch ({ errors }) {
      setIsLoading(false);
      errors &&
        errors.forEach((err) => {
          addToast(err.msg, {
            appearance: "error",
            autoDismiss: false,
          });
        });
    }
  };

  const setInterrogationInfo = (e) => {
    const { value, name } = e.target;
    setInterrogation((prev) => {
      return produce(prev, (draft) => {
        if (name === "mrn") {
          if (!draft["patientLocation"]) draft["patientLocation"] = {};
          draft.patientLocation[name] = value;
        } else draft.interrogation[name] = value;
      });
    });
  };

  const resetForms = () => {
    setInterrogation((prev) =>
      produce(prev, (draft) => {
        draft.interrogation.interrogationDate = "";
        draft.interrogation.visitNumber = "";
      })
    );
    setIntrOrderInfo({});
  };

  const setPatientInfo = (e) => {
    const { value, name } = e.target;
    setInterrogation((prev) => {
      return produce(prev, (draft) => {
        draft.patient[name] = value;
      });
    });
  };

  const validateFields = () => {
    const fieldsWithErrors = {};
    const requiredFields = {
      interrogationDate: interrogation?.interrogation?.interrogationDate,
    };

    Object.keys(requiredFields).forEach((field) => {
      if (!requiredFields[field]) {
        fieldsWithErrors[field] = true;
      }
    });

    const hasErrors = Object.keys(fieldsWithErrors).length;
    setErrors(fieldsWithErrors);

    if (!hasErrors) {
      onCreateInterrogation();
    }
  };

  return (
    <>
      <div className="modal override-row create-interrogation-modal popup" id="popupIR">
        <div className="modalContent">
          <div className="grpHdr">
            <h3>Create Interrogation</h3>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>First Name</span>
                <input
                  type="text"
                  name="name"
                  onChange={setPatientInfo}
                  value={interrogation?.patient?.name || ""}
                />
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <label>
                  <span>Last Name</span>
                  <input
                    type="text"
                    name="family"
                    onChange={setPatientInfo}
                    value={interrogation?.patient?.family || ""}
                  />
                </label>
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label className="inCal">
                <span>Date of Birth</span>
              </label>
              <DatePicker
                selected={(interrogation?.patient?.dob && new Date(interrogation?.patient?.dob)) || ""}
                onChange={(date) => {
                  setInterrogation((prev) => {
                    return produce(prev, (draft) => {
                      draft.patient["dob"] = date;
                    });
                  });
                }}
              />
            </div>
            <div className="col col-s-100 col50">
              <div className="containerFlex containerFormLi" style={{ flexDirection: "column" }}>
                <label className="title-label">
                  <span>Gender</span>
                </label>
                <div style={{ marginTop: "20px" }}>
                  <label className="formLi flex0">
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="MALE"
                      onChange={setPatientInfo}
                      checked={interrogation?.patient?.gender === "MALE"}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Male</span>
                  </label>
                  <label className="formLi flex0">
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="FEMALE"
                      onChange={setPatientInfo}
                      checked={interrogation?.patient?.gender === "FEMALE"}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Female</span>
                  </label>
                  <label className="formLi flex0">
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="UNKNOWN"
                      onChange={setPatientInfo}
                      checked={interrogation?.patient?.gender === "UNKNOWN"}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Unknown</span>
                  </label>
                  <label className="formLi flex0" style={{ marginTop: "0px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="NONBINARY"
                      onChange={setPatientInfo}
                      checked={interrogation?.patient?.gender === "NONBINARY"}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Nonbinary</span>
                  </label>
                  <label className="formLi flex0" style={{ marginTop: "0px" }}>
                    <input
                      type="radio"
                      name="gender"
                      className="chk"
                      value="OTHER"
                      onChange={setPatientInfo}
                      checked={interrogation?.patient?.gender === "OTHER"}
                    />
                    <span className="chkmrk radio" />
                    <span className="chk-label">Other</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Location</span>
                <select>
                  <option disabled>Select</option>
                  {patient?.patientLocation?.map((item) => (
                    <option key={item?.location?.id} value={item?.location?.id}>
                      {item?.location?.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>Medical Record Number</span>
                <input
                  id="mrnId"
                  type="text"
                  name="mrn"
                  onChange={setInterrogationInfo}
                  value={interrogation?.patientLocation?.mrn || ""}
                  disabled={!!patient?.patientLocation[0]?.mrn}
                />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Visit Number</span>
                <input
                  id="visitNumberId"
                  type="text"
                  name="visitNumber"
                  onChange={setInterrogationInfo}
                  value={
                    interrogation?.interrogation?.visitNumber ? interrogation?.interrogation?.visitNumber : ""
                  }
                  disabled={!!intrOrderInfo?.irVisitNumber}
                />
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>Reason for Interrogation</span>
                <input
                  id="reasonInterrogationId"
                  type="text"
                  name="interrogationReason"
                  onChange={setInterrogationInfo}
                  value={interrogation?.interrogation?.interrogationReason || ""}
                />
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label>
                <span>Device</span>
                <select
                  name="device"
                  value={interrogation?.interrogation?.device.id}
                  onChange={(e) => {
                    const { value } = e.target;
                    const device = devices.find((d) => d.id == value);
                    setInterrogation((prev) => {
                      return produce(prev, (draft) => {
                        draft.interrogation["device"].id = device.id;
                        draft.interrogation["device"].manufacturer = device.manufacturerName;
                        draft.interrogation["device"].serialNumber = device.serialNumber;
                        draft.interrogation["device"].type = device.deviceType;
                        draft.interrogation["device"].implantDate = device.implantDate;
                        draft.interrogation["device"].modelName = device.modelName;
                      });
                    });
                  }}
                >
                  {devices &&
                    devices?.map((item) => (
                      <option
                        value={item.id}
                        key={item.id}
                      >{`${item.manufacturerName} - ${item.deviceType}`}</option>
                    ))}
                </select>
              </label>
            </div>
            <div className="col col-s-100 col50">
              <label>
                <span>Transmission Type</span>
                <select
                  name="interrogationType"
                  onChange={setInterrogationInfo}
                  value={interrogation?.interrogation?.interrogationType || ""}
                >
                  <option disabled>Select</option>
                  <option value="POC">POC</option>
                  <option value="REMOTE">Remote</option>
                </select>
              </label>
            </div>
          </div>
          <div className="row rowStretch">
            <div className="col col-s-100 col50">
              <label className="inCal">
                <span>
                  Interrogation Date <sub className="required-field">*</sub>
                </span>
              </label>
              <DatePicker
                id="interrogationDateId"
                data-testid="interrogation-date-picker"
                selected={
                  interrogation?.interrogation?.interrogationDate
                    ? interrogation.interrogation.interrogationDate
                    : intrOrderInfo?.irDate || ""
                }
                onChange={(date) => {
                  setInterrogation((prev) => {
                    return produce(prev, (draft) => {
                      draft.interrogation["interrogationDate"] = date;
                    });
                  });
                  setErrors((prev) => {
                    delete prev.interrogationDate;
                    return prev;
                  });
                }}
              />
              {errors.interrogationDate && (
                <span className="error">Required fields missing data. Please review and re-submit.</span>
              )}
            </div>
            <div className="col col-s-100 col50" />
          </div>
          <div className="row rowStretch noMargin">
            <div className="col create-ir-col" style={{ display: "flex" }}>
              <button
                id="saveInterrogationBtnId"
                className="btn btnSuccess"
                onClick={validateFields}
                disabled={isLoading || devices.length === 0}
              >
                {isLoading ? (
                  <div
                    id="results-loading-icon"
                    className="saving-white-ico icon-64"
                    style={{ width: 15, height: 15, marginRight: 3 }}
                  />
                ) : (
                  <i className="ti ti-plus" />
                )}
                Create IR
              </button>
              <a href="#">
                <button className="btn btn-cancel" onClick={resetForms}>
                  Cancel
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
      <a href="#" className="close-popup"></a>
    </>
  );
};

export default CreateIRModal;
